// src/services/pedidosService.js
/**
 * Pedidos Service - Handles all purchase order-related API communication with backend
 * Backend API Base URL: Production VPS via config.js
 */

import axios from 'axios';
import { NODE_SUPPLIERS_API_URL } from '../config';

const PEDIDOS_API_URL = `${NODE_SUPPLIERS_API_URL}/orders`;

/**
 * Get all pedidos (purchase orders) with pagination and optional filters
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Records per page (default: 20)
 * @param {Object} filters - Optional filters { status, dateFrom, dateTo, search }
 * @returns {Promise} Response with pedidos list and pagination
 */
export const getAllPedidos = async (page = 1, limit = 20, filters = {}) => {
    try {
        const params = {
            page,
            limit
        };

        // Add optional filters
        if (filters.status) {
            params.status = filters.status;
        }
        if (filters.dateFrom) {
            params.dateFrom = filters.dateFrom;
        }
        if (filters.dateTo) {
            params.dateTo = filters.dateTo;
        }
        if (filters.search) {
            params.search = filters.search;
        }

        const response = await axios.get(PEDIDOS_API_URL, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching pedidos:', error);
        throw error;
    }
};

/**
 * Get pedido by ID
 * @param {number} pedidoId - Pedido ID
 * @returns {Promise} Response with pedido details
 */
export const getPedidoById = async (pedidoId) => {
    try {
        const response = await axios.get(`${PEDIDOS_API_URL}/${pedidoId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching pedido by ID:', error);
        throw error;
    }
};

/**
 * Search pedidos by supplier name or pedido number
 * @param {string} searchTerm - Search term
 * @param {number} maxResults - Maximum results to return (default: 50)
 * @returns {Promise} Response with matching pedidos
 */
export const searchPedidos = async (searchTerm, maxResults = 50) => {
    try {
        if (!searchTerm || searchTerm.trim().length === 0) {
            return { results: [], total_count: 0 };
        }

        const response = await axios.get(
            `${PEDIDOS_API_URL}/search/${encodeURIComponent(searchTerm)}`,
            { params: { maxResults } }
        );
        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 404) {
            return { results: [], total_count: 0 };
        }
        console.error('Error searching pedidos:', error);
        throw error;
    }
};

/**
 * Create new pedido (purchase order)
 * @param {Object} pedidoData - Pedido data object
 * @returns {Promise} Response with created pedido
 */
export const createPedido = async (pedidoData) => {
    try {
        const response = await axios.post(`${PEDIDOS_API_URL}/create`, pedidoData);
        return response.data;
    } catch (error) {
        console.error('Error creating pedido:', error);
        throw error;
    }
};

/**
 * Update existing pedido
 * @param {number} pedidoId - Pedido ID
 * @param {Object} updates - Fields to update
 * @returns {Promise} Response with updated pedido
 */
export const updatePedido = async (pedidoId, updates) => {
    try {
        const response = await axios.put(`${PEDIDOS_API_URL}/${pedidoId}`, updates);
        return response.data;
    } catch (error) {
        console.error('Error updating pedido:', error);
        throw error;
    }
};

/**
 * Update pedido status
 * @param {number} pedidoId - Pedido ID
 * @param {string} newStatus - New status value
 * @returns {Promise} Response with updated pedido
 */
export const updatePedidoStatus = async (pedidoId, newStatus) => {
    try {
        const response = await axios.patch(`${PEDIDOS_API_URL}/${pedidoId}/status`, {
            status: newStatus
        });
        return response.data;
    } catch (error) {
        console.error('Error updating pedido status:', error);
        throw error;
    }
};

/**
 * Cancel pedido
 * @param {number} pedidoId - Pedido ID
 * @param {string} reason - Cancellation reason (optional)
 * @returns {Promise} Response with cancelled pedido
 */
export const cancelPedido = async (pedidoId, reason = '') => {
    try {
        const response = await axios.post(`${PEDIDOS_API_URL}/${pedidoId}/cancel`, {
            reason
        });
        return response.data;
    } catch (error) {
        console.error('Error cancelling pedido:', error);
        throw error;
    }
};

/**
 * Get pedido items/products
 * @param {number} pedidoId - Pedido ID
 * @returns {Promise} Response with pedido items
 */
export const getPedidoItems = async (pedidoId) => {
    try {
        const response = await axios.get(`${PEDIDOS_API_URL}/${pedidoId}/items`);
        return response.data;
    } catch (error) {
        console.error('Error fetching pedido items:', error);
        throw error;
    }
};

/**
 * Update order items with full replacement logic and audit trail
 * PUT /apitest/orders/:id/items - Requires ClientAdministrator role
 * 
 * @param {number} orderId - Order ID
 * @param {Object} data - Update data object
 * @param {string} data.change_reason - REQUIRED: Audit reason for the change
 * @param {Array} data.items - REQUIRED: Complete list of items (full replacement)
 * @param {Object} [data.tax_fields] - Optional tax fields
 * @param {boolean} [data.tax_fields.cargas_impositivas_on] - Enable tax charges
 * @param {number} [data.tax_fields.retencion] - Retention amount (subtracts from total)
 * @param {number} [data.tax_fields.percepcion] - Perception amount (adds to total)
 * @param {number} [data.tax_fields.iibb_directo] - IIBB direct amount (adds to total)
 * @returns {Promise} Response with updated order and audit info
 * 
 * Item fields:
 * - product_id: number|null (null for custom products)
 * - product_code: string (optional)
 * - product_description: string (required for custom products)
 * - quantity: number (required, > 0)
 * - unit_price: number (required)
 * - bulk_price: number (optional, used if > 0)
 * - iva_percentage: number (0, 10.5, or 21 - default 21)
 * - discount_percentage: number (0-99.99%)
 * - notes: string (optional)
 */
export const updateOrderItems = async (orderId, data) => {
    try {
        const response = await axios.put(`${PEDIDOS_API_URL}/${orderId}/items`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating order items:', error);
        // Preserve backend error messages for display
        if (error.response?.data?.message) {
            throw new Error(error.response.data.message);
        }
        throw error;
    }
};

/**
 * Get pedidos by supplier ID with pagination and summary
 * @param {number} supplierId - Supplier ID
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Records per page (default: 20)
 * @param {Object} filters - Optional filters { status, dateFrom, dateTo }
 * @returns {Promise} Response with supplier info, summary, orders, and pagination
 */
export const getPedidosBySupplier = async (supplierId, page = 1, limit = 20, filters = {}) => {
    try {
        const params = {
            page,
            limit
        };

        if (filters.status) params.status = filters.status;
        if (filters.dateFrom) params.dateFrom = filters.dateFrom;
        if (filters.dateTo) params.dateTo = filters.dateTo;

        const response = await axios.get(`${PEDIDOS_API_URL}/supplier/${supplierId}`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching pedidos by supplier:', error);
        throw error;
    }
};

/**
 * Delete pedido
 * @param {number} pedidoId - Pedido ID
 * @returns {Promise} Response with deletion confirmation
 */
export const deletePedido = async (pedidoId) => {
    try {
        const response = await axios.delete(`${PEDIDOS_API_URL}/${pedidoId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting pedido:', error);
        throw error;
    }
};

/**
 * Get supplier accounts payable records
 * @param {Object} params - Query parameters { page, limit, estado, supplier_id, overdue_only }
 * @returns {Promise} Response with AP records
 */
export const getCuentasCorrientesProveedores = async (params = {}) => {
    try {
        const response = await axios.get(
            `${NODE_SUPPLIERS_API_URL}/cuentas-corrientes-proveedores`,
            { params }
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching AP records:', error);
        throw error;
    }
};

/**
 * Register payment for supplier AP record
 * @param {number} apId - AP record ID
 * @param {Object} paymentData - { monto, fecha_pago, metodo_pago, notas }
 * @returns {Promise} Response with updated AP record
 */
export const registerAPPayment = async (apId, paymentData) => {
    try {
        const response = await axios.post(
            `${NODE_SUPPLIERS_API_URL}/cuentas-corrientes-proveedores/${apId}/payment`,
            paymentData
        );
        return response.data;
    } catch (error) {
        console.error('Error registering payment:', error);
        throw error;
    }
};

/**
 * Register a payment for a purchase order (partial, full, on-delivery)
 * @param {number} orderId - Order ID
 * @param {Object} paymentData - { payment_date, amount, payment_method?, notes? }
 * @returns {Promise} Response with updated order payment summary
 */
export const addOrderPayment = async (orderId, paymentData) => {
    try {
        const response = await axios.post(`${PEDIDOS_API_URL}/${orderId}/payments`, paymentData);
        return response.data;
    } catch (error) {
        console.error('Error registering order payment:', error);
        throw error;
    }
};

/**
 * Get all payment records for a purchase order
 * @param {number} orderId - Order ID
 * @returns {Promise} Response with { summary, payments[] }
 */
export const getOrderPayments = async (orderId) => {
    try {
        const response = await axios.get(`${PEDIDOS_API_URL}/${orderId}/payments`);
        return response.data;
    } catch (error) {
        console.error('Error fetching order payments:', error);
        throw error;
    }
};
